import { CICLO, SETORES, UNIDADES } from "@caint/data";
import { EixoChip, Gauge, Header, Icon, Logo, Meter, Row, SealMedal, SealPill } from "@caint/design-system";
import type { ChaveSelo } from "@caint/engine";
import { Fragment } from "react";
import type { AvaliacaoRemota } from "../lib/api.ts";
import { engine } from "../lib/engine.ts";
import type { ItemMerged, UsuarioAutenticado } from "../state/useAppState.ts";

const COR_SELO: Record<string, string> = {
  ouro: "var(--seal-ouro)",
  prata: "var(--seal-prata)",
  bronze: "var(--seal-bronze)",
  dev: "var(--seal-dev)",
  pend: "var(--slate-400)",
};

export interface DashboardProps {
  auth: UsuarioAutenticado;
  items: ItemMerged[];
  qaci: number;
  ims: number;
  go: (route: string, arg?: string) => void;
  onLogout: () => void;
  avaliacaoAtual?: AvaliacaoRemota;
}

export function Dashboard({ items, qaci, ims, go, onLogout, avaliacaoAtual }: DashboardProps) {
  const ativa = UNIDADES.find((u) => u.id === avaliacaoAtual?.unidadeId) ?? UNIDADES[0];
  if (!ativa) {
    return null;
  }
  const calc = engine.imgq(items, "mi", qaci, ims);
  const selo = engine.selo(calc.ajustado);
  const nc = engine.contarConf(items, "NC", "mi");
  const coreCriticos = calc.core0 + calc.core1;
  const riscosAltos = items.filter((it) => {
    const r = engine.riscoResidual(it, "mi");
    return r != null && r >= 8;
  }).length;

  const eixos: [string, number | null][] = [
    ["IMGO", calc.imgo],
    ["IMQA", calc.imqa],
    ["IMGR", calc.imgr],
  ];

  return (
    <Fragment>
      <Header
        ink
        left={
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <Logo size={28} variant="reverse" />
            <div>
              <div
                style={{
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 17,
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                }}
              >
                CAINT
              </div>
              <div style={{ color: "var(--teal-300)", fontSize: 10 }}>{CICLO.nome}</div>
            </div>
          </div>
        }
        action={
          <button type="button" className="m-h-icon" onClick={onLogout} aria-label="Sair">
            <Icon name="logout" size={17} color="var(--text-muted)" />
          </button>
        }
      />
      <div className="m-body" style={{ background: "var(--surface-ink)", paddingTop: 14 }}>
        <div className="m-hero">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div style={{ flex: 1 }}>
              <div className="h-eyebrow">Sua unidade · {ativa.fase ?? ativa.tipo}</div>
              <div className="h-name">{ativa.nome}</div>
              <div className="h-meta">
                {ativa.municipio}
                {ativa.uf ? ` – ${ativa.uf}` : ""} · {ativa.tipo}
              </div>
            </div>
            <SealMedal chave={selo.chave} roman={selo.romano} size={54} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 16 }}>
            <Gauge value={calc.ajustado} size={104} label="IMGQ" />
            <div style={{ flex: 1 }}>
              {eixos.map(([k, v]) => (
                <div
                  key={k}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "5px 0",
                    borderBottom: "0.5px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <EixoChip eixo={k as "IMGO" | "IMQA" | "IMGR"} />
                  <b
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 15,
                      color: v != null && v < 0.46 ? "var(--coral-300)" : "#fff",
                    }}
                  >
                    {engine.num2(v)}
                  </b>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  paddingTop: 7,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Selo previsto</span>
                <SealPill chave={selo.chave} />
              </div>
            </div>
          </div>
          {calc.travado && (
            <div
              style={{
                marginTop: 14,
                background: "rgba(192,57,43,0.2)",
                border: "0.5px solid rgba(255,255,255,0.15)",
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 12.5,
                color: "#fff",
              }}
            >
              ⛔ {coreCriticos > 0 ? `${coreCriticos} item(ns) CORE em Mat 0/1` : "Eixo abaixo da meta"} —
              selo travado em ≤ 59,9%
            </div>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <Meter value={calc.ajustado} />
        </div>

        <div className="m-kpis" style={{ marginTop: 18 }}>
          <div className="m-kpi">
            <div className="k-l">Não Conformidades</div>
            <div className="k-v">{nc}</div>
            <div className="k-f">geram Plano de Ação</div>
          </div>
          <div className="m-kpi">
            <div className="k-l">Riscos ≥ 8</div>
            <div className="k-v" style={{ color: riscosAltos ? "var(--risk-ra)" : "var(--text)" }}>
              {riscosAltos}
            </div>
            <div className="k-f">plano obrigatório</div>
          </div>
          <div className="m-kpi">
            <div className="k-l">CORE Mat 0/1</div>
            <div className="k-v" style={{ color: coreCriticos ? "var(--danger)" : "var(--success)" }}>
              {coreCriticos}
            </div>
            <div className="k-f">travam o selo</div>
          </div>
          <div className="m-kpi">
            <div className="k-l">IMGQ Bruto</div>
            <div className="k-v" style={{ color: "var(--primary)" }}>
              {engine.pct(calc.bruto).replace("%", "")}
              <span style={{ fontSize: 15 }}>%</span>
            </div>
            <div className="k-f">antes da exclusão</div>
          </div>
        </div>

        <div className="m-section-t">Ações rápidas</div>
        <div className="m-list">
          <Row
            icon="check"
            iconBg="var(--mat-3)"
            title="Continuar checklist"
            sub={`${items.length} itens · ${SETORES.length} setores`}
            onClick={() => go("checklist")}
          />
          <Row
            icon="risk"
            iconBg="var(--risk-ra)"
            title="Riscos prioritários"
            sub={`${riscosAltos} itens com risco ≥ 8`}
            onClick={() => go("risco")}
          />
          <Row
            icon="report"
            iconBg="var(--primary)"
            title="Relatório executivo"
            sub="Consolidação final"
            onClick={() => go("relatorio")}
          />
        </div>

        <div className="m-section-t">Rede · {UNIDADES.length} unidades</div>
        <div className="m-list">
          {UNIDADES.slice(0, 6).map((u) => {
            const ehAtiva = u.id === ativa.id;
            const valor = ehAtiva ? calc.ajustado : (u.imgq ?? null);
            const chave: ChaveSelo = ehAtiva ? selo.chave : ((u.selo as ChaveSelo) ?? "pend");
            return (
              <Row
                key={u.id}
                title={u.nome}
                sub={`${u.municipio} · ${u.tipo}`}
                onClick={() => go("unit", u.id)}
                right={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <b style={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{engine.pct(valor)}</b>
                    <span
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        background: COR_SELO[chave] ?? "var(--slate-400)",
                      }}
                    />
                  </div>
                }
              />
            );
          })}
        </div>
      </div>
    </Fragment>
  );
}
